import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import Timeout = NodeJS.Timeout;

import { NeptuneApi } from '../../api/genericApi/neptune.api';
import { ConfigService } from '../../Core/Config/services/config.service';
import { HttpVerbsEnum } from '../../api/shared/enums/http-verbs.enum';
import { environment } from '../../../environments/environment';
import { FrontendBroadcastModel } from '../models/frontend-broadcast.model';


export enum ResourceType {
    targetingLists = 'targetinglists',
    targetingSearches = 'targetingsearches',
    distributions = 'distributions',
    savedMentionSearches = 'saved-mention-searches',
    socialSubscriptions = 'socialsubscriptions',
    briefings = 'briefings',
    sharedReports = 'shared-reports',
    reports = 'reports',
    newswire = 'newswire',
    media = 'media',
    filedownloadsMonitoring = 'filedownloads/monitoring',
    autogenerateinsights = 'autogenerateinsights',
}

@Injectable()
export class WebsocketAwareNeptuneService extends NeptuneApi {

    readonly tabSessionId: string = crypto?.randomUUID();

    private websocketConnection = null;
    private websocketMessages$ = new BehaviorSubject(null);

    private connectionErrorCount: number = 0;
    private retryTimeout: Timeout;
    private accountNotPermittedErrorReason = 'Account not permitted to connect';

    private messageMap: Map<string, Timeout> = new Map();

    constructor(
        public httpClient: HttpClient,
        public cookieService: CookieService,
        public configurationService: ConfigService
    ) {
        super(httpClient, cookieService, configurationService);

        this.connectToWebsocket();
    }

    api<T>(
        slug: string,
        data: any,
        method: HttpVerbsEnum = HttpVerbsEnum.GET,
        query: object = {},
        responseType?: 'arraybuffer' | 'blob' | 'json' | 'text',
        headers?: HttpHeaders,
        version?: string
    ): Observable<T> {
        headers = (headers ?? new HttpHeaders()).set('x-tab-session-id', this.tabSessionId);

        return super.api(slug, data, method, query, responseType, headers, version).pipe<T>(
            tap(
                (response: any) => {
                    if (method !== HttpVerbsEnum.GET) {
                        this.sendWebsocketEventBatched(slug, method);
                    }
                }
            )
        );
    }

    public getWebsocketMessages(): Observable<any> {
        return this.websocketMessages$;
    }

    public connectToWebsocket(resetCount: boolean = false): void {
        if (this.retryTimeout) {
            clearTimeout(this.retryTimeout);
        }
        if (this.websocketConnection) {
            this.disconnectFromWebsocket();
        }
        if (resetCount) {
            this.connectionErrorCount = 0;
        }
        this.websocketConnection = new WebSocket(environment.websocketUrl);

        this.websocketConnection.addEventListener('error', (error: any) => {
            console.error('Websocket error: ', error);
            if (this.websocketConnection) {
                this.websocketConnection.close();
            }
            this.websocketConnection = null;
        });

        this.websocketConnection.addEventListener('close', (event: any) => {
            if (event.code === 1008 && event.reason === this.accountNotPermittedErrorReason) {
                // This account isn't allowed to connect to websockets, don't retry
                return;
            }
            this.connectionErrorCount++;
            if (this.connectionErrorCount < 5) {
                this.retryTimeout = setTimeout(
                    () => {
                        this.connectToWebsocket();
                    },
                    this.connectionErrorCount * 1000
                );
            }
        });

        this.websocketConnection.addEventListener('message', (message: any) => {
            try {
                const parsedMessage = JSON.parse(message.data);
                this.websocketMessages$.next(parsedMessage);
            } catch (err) {
                console.warn('got a bad websocket message:', message);
            }
        });
    }

    public disconnectFromWebsocket(): void {
        if (this.websocketConnection) {
            this.websocketConnection.close();
        }
        this.connectionErrorCount = 0;
    }

    /**
     * Batched version of sending webhook events, generally used in an automated fashion
     *
     * @param slug
     * @param method
     */
    public sendWebsocketEventBatched(
        slug: string,
        method: HttpVerbsEnum
    ) {
        const resourceTypeValue = slug.split('/').find(a => a.length);
        const message: FrontendBroadcastModel = {
            resourceType: resourceTypeValue,
            resourceAction: method,
            slug: slug
        };

        // Batch up notifications, in case we're doing a lot of the same POST/PUT in a row, only notify about a specific resource type every 250ms maximum
        if (this.messageMap.has(message.resourceType)) {
            return;
        } else {
            this.messageMap.set(
                message.resourceType,
                setTimeout(
                    () => {
                        this.websocketConnection?.send(JSON.stringify(message));
                        this.messageMap.delete(message.resourceType);
                    }, 250
                )
            );
        }
    }

    /**
     * Unbatched, used in cases that the websocket event needs to happen as soon as possible
     *
     * @param slug
     * @param method
     */
    public sendWebsocketEventInstant(
        slug: string,
        method: HttpVerbsEnum
    ) {
        const resourceTypeValue = slug.split('/').find(a => a.length);
        const message: FrontendBroadcastModel = {
            resourceType: resourceTypeValue,
            resourceAction: method,
            slug: slug
        };

        this.websocketConnection?.send(JSON.stringify(message));
    }
}
